import React, { useEffect, useState } from "react";
import { axiosInstance } from "common/network";
import {
  Box,
  Stack,
  TextField,
  FormControlLabel,
  Switch,
  Modal,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import { PRIMARY_COLOR_HEX } from "theme/palette";

// Modal to issue a new Popul8 license: pick the duration and choose whether to
// email it to the account owner. Kept compact/professional while retaining the
// portal's identity (primary-colour header accent and primary action button).
const EmailLicenseModal = (props) => {
  const { accountUuid, emailAddress } = props;
  const [open, setOpen] = useState(false);
  const [durationDays, setDurationDays] = useState("30");
  const [sendEmail, setSendEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setOpen(props.open);
    if (props.open) {
      // Reset to defaults each time the modal is opened.
      setDurationDays("30");
      setSendEmail(false);
      setSubmitting(false);
    }
  }, [props.open]);

  const handleClose = () => props.handleClose();

  const durationValid = () => {
    const n = parseInt(durationDays, 10);
    return !isNaN(n) && n > 0;
  };

  const finish = () => {
    setSubmitting(false);
    handleClose();
    props.handleSuccess();
  };

  const handleConfirm = () => {
    if (!durationValid()) return;
    setSubmitting(true);

    const body = {
      duration: parseInt(durationDays, 10),
      send_via_email: sendEmail,
      account_uuid: accountUuid,
      email_address: emailAddress,
    };
    const url =
      process.env.REACT_APP_API_ROOT +
      `v3/licensed_applications/popul8/license_files`;

    // Either way we only create the license; the new row appears in the refreshed
    // list, where it can be downloaded on demand. When emailing, the API returns a
    // short status string we surface to the admin.
    axiosInstance
      .post(url, body)
      .then((response) => {
        if (sendEmail) alert(response.data);
        finish();
      })
      .catch((e) => {
        console.log("issueLicense error:", e);
        setSubmitting(false);
        handleClose();
      });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="issue-license-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100%",
          maxWidth: 420,
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 24,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
          <Typography
            id="issue-license-title"
            variant="h6"
            sx={{ fontWeight: 600 }}
          >
            Issue Popul8 license
          </Typography>
        </Box>
        <Divider sx={{ borderBottomWidth: 3, borderColor: PRIMARY_COLOR_HEX }} />

        <Stack sx={{ px: 3, py: 2.5 }} spacing={2}>
          <TextField
            label="Duration (days)"
            type="number"
            size="small"
            value={durationDays}
            onChange={(e) => setDurationDays(e.target.value)}
            error={!durationValid()}
            helperText={!durationValid() ? "Must be a number greater than 0" : " "}
            inputProps={{ min: 1 }}
            fullWidth
          />

          <FormControlLabel
            control={
              <Switch
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: PRIMARY_COLOR_HEX,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: PRIMARY_COLOR_HEX,
                  },
                }}
              />
            }
            label={
              <Typography variant="body2">
                {sendEmail
                  ? `Send via email to ${emailAddress || "the account owner"}`
                  : "Create without emailing (download later from the list)"}
              </Typography>
            }
          />
        </Stack>

        <Divider />
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{ px: 3, py: 2 }}
        >
          <Button onClick={handleClose} sx={{ color: PRIMARY_COLOR_HEX }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleConfirm}
            disabled={!durationValid() || submitting}
            sx={{
              bgcolor: PRIMARY_COLOR_HEX,
              color: "#fff",
              "&:hover": { bgcolor: PRIMARY_COLOR_HEX },
              "&.Mui-disabled": {
                bgcolor: "rgba(0,0,0,0.12)",
                color: "rgba(0,0,0,0.26)",
              },
            }}
          >
            {sendEmail ? "Send" : "Create"}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default EmailLicenseModal;
